import { Country } from "src/components/Context/CountryContext";
import { InventoryTransaction, Promotion } from "src/models/product";
import { dateStrToMoment } from "../../utils/formatUtil";
import {
  getSearchAsync,
  getSearchMessagingProductsAsync,
  getProductAsync,
  getBrandImageAsync,
  postBrandImageAsync,
  postWasPricesAsync,
  getPurchaseOrdersAsync,
  getPromosAsync,
  getInventoryTransactionsAsync,
  patchProductAsync,
  postPinnedProducts,
  getPinnedProducts,
  postMyListItem,
  getMyLists,
  getMyListDetails,
  deleteMyList,
  deleteMyListItem,
  getInitialAppData,
  getReleaseNotes,
  getMPPricingHistoryAsync,
  getJBPricingHistoryAsync,
  getMessageByIdAsync,
  postMessageTemplateCreateAsync,
  putMessageTemplateUpdateAsync,
  postMessageImageAsync,
  getSearchMessagingProductsBySkusAsync,
  postMessageImageCopyAsync,
  getProductHistoryAsync,
  postStaffPriceExceptionsAsync,
  getProductGroupAsync,
  getProductCategoryAsync,
  postProductSyncMappingsAsync,
  postProductSyncSameLocationAsync,
  getPhysicalUserStoreLocationAsync,
  postProductSyncFromProdAsync,
  getSearchProductionSkuAsync,
} from "../../utils/productAppApiHelper";
import { GetProductResponse, GetPurchaseOrdersResponse } from "src/models/product";
import {
  GetAppDataResponse,
  GetProductListProductResponse,
  ProductList,
  PhysicalUserStoreLocationResponse,
} from "src/models/appData";
import { PaginatedSearchResults, SearchResponse, SearchResult, SearchResults } from "src/models/search";
import { sanitiseString } from "src/utils/stringUtils";
import { MPPricingHistoryResponse } from "src/models/product/MPPricingHistoryResponse";
import { JBPricingHistoryResponse } from "src/models/product/JBPricingHistoryResponse";
import { CreateMessageModel } from "src/validationSchemas";
import { ProductGroupResponse } from "src/models/product/ProductGroupResponse";
import { ProductCategoryResponse } from "src/models/product/ProductCategoryResponse";
import { InventoryTransactionsResult } from "src/models/product/InventoryTransactionsResult";
import moment from "moment";
import { ProductHistoryResponse } from "../../models/product/ProductHistoryResponse";
import { WasPriceOverrideResponse } from "src/models/product/WasPriceOverrideResponse";

const fetchSearchAsync = async (
  query: string,
  jbPage: number,
  mpPage: number,
  pageSize: number,
  country: Country
): Promise<SearchResponse> => {
  try {
    return await getSearchAsync(query, jbPage, mpPage, pageSize, country);
  } catch (error) {
    return {
      totalRows: 0,
      jbResults: { rows: 0, results: [] },
      mpResults: { rows: 0, results: [] },
    } as SearchResponse;
  }
};

const fetchMessagingProductsAsync = async (
  query: string,
  page: number,
  pageSize: number,
  country: Country
): Promise<SearchResults> => {
  try {
    return await getSearchMessagingProductsAsync(query, page, pageSize, country);
  } catch (error) {
    return {
      rows: 0,
      results: [],
    } as SearchResults;
  }
};

const fetchMessagingProductsBySkusAsync = async (
  skus: string[],
  country: Country,
  page: number = 1,
  pageSize: number = 10
): Promise<PaginatedSearchResults> => {
  try {
    return await getSearchMessagingProductsBySkusAsync(skus, country, page, pageSize);
  } catch (error) {
    console.error("Error in fetchMessagingProductsBySkusAsync:", error);
    return {
      totalCount: 0,
      page: page,
      pageSize: pageSize,
      results: [],
    } as PaginatedSearchResults;
  }
};

const fetchProductDetailAsync = async (
  sku: string,
  country: Country,
  locationId?: number
): Promise<GetProductResponse> => {
  return await getProductAsync(sku, country, locationId);
};

const fetchJBPricingHistoryAsync = async (sku: string, country: Country): Promise<JBPricingHistoryResponse[]> => {
  return await getJBPricingHistoryAsync(sku, country);
};

const fetchProductHistoryAsync = async (sku: string, country: Country): Promise<ProductHistoryResponse> => {
  return await getProductHistoryAsync(sku, country);
};

const fetchMPPricingHistoryAsync = async (sku: string, country: Country): Promise<MPPricingHistoryResponse> => {
  return await getMPPricingHistoryAsync(sku, country);
};

const fetchBrandImageAsync = async (brandName: string, country: Country): Promise<string> => {
  return await getBrandImageAsync(brandName, country);
};

const updateBrandImageAsync = async (brandName: string, image: any, country: Country) => {
  return await postBrandImageAsync(brandName, image, country);
};

const sendWasPricesAsync = async (wasPrices, country): Promise<WasPriceOverrideResponse | null> => {
  return await postWasPricesAsync(wasPrices, country);
};

const fetchMessageByIdAsync = async (id: string, country: Country): Promise<CreateMessageModel> => {
  return await getMessageByIdAsync(id, country);
};

const createMessageTemplateAsync = async (requestObject: any, country: Country): Promise<any> => {
  return await postMessageTemplateCreateAsync(requestObject, country);
};

const updateMessageTemplateAsync = async (id: string, requestObject: any, country: Country): Promise<any> => {
  return await putMessageTemplateUpdateAsync(id, requestObject, country);
};

const fetchInventoryTransactionsAsync = async (
  sku: string,
  locationId: number,
  country: Country,
  includeActive: boolean
): Promise<InventoryTransactionsResult> => {
  try {
    const response = await getInventoryTransactionsAsync(sku, locationId, country, includeActive);
    if (!response) return null;

    let activeTransactions: InventoryTransaction[] = [];
    if (response.activeTransactions) {
      activeTransactions = response.activeTransactions?.map((t) => {
        return {
          id: t.id,
          sku: t.sku,
          locationId: t.locationId,
          transactionType: t.transactionType,
          operationType: t.createdOperationType,
          timestamp: t.displayTimestamp,
          externalItemIdentifier: t.externalReference ?? t.externalItemIdentifier,
          quantity: t.quantity,
          createdByUserId: t.createdByUserId,
          channel: t.channel,
          status: t.transactionStatus,
        } as InventoryTransaction;
      });
    }

    return {
      lastStockInReceivedDate: response.lastStockInReceivedDate
        ? moment(response.lastStockInReceivedDate).format("DD/MM/YYYY")
        : null,
      lastStockInQuantity: response.lastStockInQuantity,
      recentSales: response.recentSales,
      activeTransactions: activeTransactions,
    } as InventoryTransactionsResult;
  } catch (error) {
    console.log(error);
  }
  return null;
};

const fetchPurchaseOrdersAsync = async (sku: string, country: Country): Promise<GetPurchaseOrdersResponse[]> => {
  try {
    const response = await getPurchaseOrdersAsync(sku, country);
    return response;
  } catch (error) {
    console.log(error);
  }
  return [];
};

const fetchPromosAsync = async (sku: string, locationId: number, country: Country): Promise<Promotion[]> => {
  try {
    const response = await getPromosAsync(sku, locationId, country);
    const promos = response.map((p) => {
      return {
        id: p.promoId,
        description: p.description.trim(),
        dateFrom: dateStrToMoment(p.from),
        dateTo: dateStrToMoment(p.to),
        price: p.price,
      } as Promotion;
    });
    return promos;
  } catch (error) {
    console.log(error);
  }
  return [];
};

const patchProductDetailAsync = async (product, country) => {
  var patch = {
    seo: product.seo,
  };
  return await patchProductAsync(patch, product.sku, country);
};

const fetchPinnedProductsAsync = async (country: Country): Promise<SearchResult[]> => {
  return await getPinnedProducts(country);
};

const updatePinnedProductsAsync = async (pinnedProducts: string[], country: Country) => {
  return await postPinnedProducts(pinnedProducts, country);
};

const fetchMyListsAsync = async (country: Country): Promise<ProductList[]> => {
  const myLists = await getMyLists(country);
  const renderedMyLists = myLists?.map((m) => {
    return {
      id: sanitiseString(m.name),
      name: m.name,
      numberOfValues: m.values?.length ?? 0,
      values: m.values,
    } as ProductList;
  });
  return renderedMyLists;
};

const fetchMyListDetailsAsync = async (name: string, country: Country): Promise<GetProductListProductResponse[]> => {
  return await getMyListDetails(name, country);
};

const updateMyListAsync = async (name: string, sku: string, country: Country) => {
  const requestObject = { name: name, value: sku };
  return await postMyListItem(requestObject, country);
};

const deleteMyListAsync = async (name: string, country: Country): Promise<void> => {
  const requestObject = { name };
  return await deleteMyList(requestObject, country);
};

const deleteMyListItemAsync = async (name: string, sku: string, country: Country) => {
  const requestObject = { name: name, value: sku };
  return await deleteMyListItem(requestObject, country);
};

const fetchInitialAppDataAsync = async (country: Country): Promise<GetAppDataResponse> => {
  return await getInitialAppData(country);
};

const fetchReleaseNotesAsync = async (country: Country): Promise<any[]> => {
  return await getReleaseNotes(country);
};

const uploadMessageImageAsync = async (image: File, country: Country): Promise<any> => {
  return await postMessageImageAsync(image, country);
};

const copyMessageImageAsync = async (image: any, country: Country): Promise<any> => {
  return await postMessageImageCopyAsync(image, country);
};

const upsertStaffPriceExceptionsAsync = async (requestObject: any, country: Country): Promise<any> => {
  return await postStaffPriceExceptionsAsync(requestObject, country);
};

const fetchProductGroupAsync = async (
  country: Country,
  id?: string,
  name?: string
): Promise<ProductGroupResponse[]> => {
  return await getProductGroupAsync(id, name, country);
};

const fetchProductCategoryAsync = async (
  country: Country,
  id?: string,
  name?: string
): Promise<ProductCategoryResponse[]> => {
  return await getProductCategoryAsync(id, name, country);
};

const fetchPhysicalUserStoreLocationAsync = async (country: Country): Promise<PhysicalUserStoreLocationResponse> => {
  try {
    return await getPhysicalUserStoreLocationAsync(country);
  } catch (error) {
    console.log(error);
    return null;
  }
};

const fetchProductionSearchAsync = async (
  query: string,
  jbPage: number,
  pageSize: number,
  country: Country
): Promise<SearchResults> => {
  try {
    return await getSearchProductionSkuAsync(query, jbPage, pageSize, country);
  } catch (error) {
    return {
      rows: 0,
      results: [],
    } as SearchResults;
  }
};

export {
  fetchSearchAsync,
  fetchMessagingProductsAsync,
  fetchMessagingProductsBySkusAsync,
  fetchProductDetailAsync,
  fetchBrandImageAsync,
  updateBrandImageAsync,
  sendWasPricesAsync,
  fetchPurchaseOrdersAsync,
  fetchPromosAsync,
  fetchInventoryTransactionsAsync,
  patchProductDetailAsync,
  fetchPinnedProductsAsync,
  updatePinnedProductsAsync,
  fetchMyListsAsync,
  fetchMyListDetailsAsync,
  updateMyListAsync,
  deleteMyListAsync,
  deleteMyListItemAsync,
  fetchInitialAppDataAsync,
  fetchReleaseNotesAsync,
  fetchMPPricingHistoryAsync,
  fetchJBPricingHistoryAsync,
  fetchProductHistoryAsync,
  fetchMessageByIdAsync,
  createMessageTemplateAsync,
  updateMessageTemplateAsync,
  uploadMessageImageAsync,
  copyMessageImageAsync,
  upsertStaffPriceExceptionsAsync,
  fetchProductGroupAsync,
  fetchProductCategoryAsync,
  postProductSyncMappingsAsync,
  postProductSyncSameLocationAsync,
  fetchPhysicalUserStoreLocationAsync,
  postProductSyncFromProdAsync,
  fetchProductionSearchAsync,
};
